import { useUser } from '@myosh/myosh-login';
import {
  DataGrid,
  type DataGridDataRowTemplateProps,
  type DataGridRef,
  type DataGridSettings,
  DataSearchLocation,
  type DynamicFormFieldType,
  type JsonDataWrapper,
  type OdinDataRetrieval,
  type OdinDataRetrievalOptions,
  OdinDataSender,
  type RowTemplateType,
} from '@myosh/odin-components';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SearchTableFilters } from '../../@types/global-search';
import type { Module } from '../../@types/modules';
import { RecordTitle } from '../../components/record/record-title.component';
import { constructRecordTitle } from '../../components/record/util/record-component-util';
import useActiveRecordRef from '../../hooks/use-active-record-ref';
import useDoubleClick from '../../hooks/use-double-click';
import { useAppSelector } from '../../redux/hooks';
import { useGetAdvancedGlobalSearchMutation } from '../../redux/services/global-search';
import { useModulesQuery } from '../../redux/services/module';
import { selectActiveRecordId } from '../../redux/slices/layout';
import { setDataGridReference } from '../../services/data-grid.service';
import { forceAssert } from '../../utils/type-guards';
import { globalSearchColumns, transformFilters } from './utils';

type RecordItem = {
  recordId: number;
  module: {
    id: number;
    caption: string;
  };
  form: {
    id: number;
    caption: string;
  };
  description: string;
  docNo: number;
  creationDate: string;
  lastChange: string;
};

interface SimpleSearchProps {
  searchValue?: string;
}

export default function SimpleSearch({ searchValue }: SimpleSearchProps) {
  const { activeRecordReference } = useActiveRecordRef();
  const { rowClickHandler } = useDoubleClick();
  const { state: userState } = useUser();

  const activeRecordId = useAppSelector(selectActiveRecordId);

  const { data: modules } = useModulesQuery({ includeFormList: true });

  const [gridOptions, setGridOptions] = useState<OdinDataRetrievalOptions>();

  const gridSubscriber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const gridRef = useRef<DataGridRef | undefined>(undefined);
  const [getAdvancedGlobalSearch] = useGetAdvancedGlobalSearchMutation();

  const gridData = useMemo<OdinDataRetrieval>(() => {
    return {
      getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options?.fieldType !== 'COMBOBOX') {
          setGridOptions(options);
        }
      },

      getSubscriber: (subscriber, fieldType?: DynamicFormFieldType) => {
        if (fieldType !== 'COMBOBOX') {
          gridSubscriber.current = subscriber;
        }
      },
    };
  }, []);

  useEffect(() => {
    getAdvancedGlobalSearch({
      terms: searchValue,
      page: gridOptions?.page ?? 1,
      pageSize: gridOptions?.pageSize ?? 50,
      ...(gridOptions?.fieldFilters
        ? { searchTableFilters: forceAssert<SearchTableFilters>(transformFilters(gridOptions?.fieldFilters)) }
        : {}),
    })
      .unwrap()
      .then((res) => {
        if (res?.length > 0) {
          gridSubscriber.current?.sendData({
            data: res,
            requestId: gridOptions?.requestId,
          });
        } else {
          gridSubscriber.current?.sendData();
        }
        onDataLoadCompleted();
      })
      .catch(() => {
        gridSubscriber.current?.sendData();
      });
  }, [gridOptions]);

  useEffect(() => {
    if (activeRecordId) {
      gridRef.current?.api.data.addSelectedRows([{ id: activeRecordId }], true);
    }
  }, [activeRecordId]);

  const modulesMap = useMemo(() => {
    return modules?.reduce(
      (acc, module) => {
        acc[module.id] = module;
        return acc;
      },
      {} as Record<number, Module>
    );
  }, [modules]);

  const rowTemplate: RowTemplateType = useCallback(
    (props: DataGridDataRowTemplateProps, children: React.ReactNode) => {
      const rowData = props.data as RecordItem;
      const module = modulesMap?.[rowData.module.id];

      const handleClick = (isFullScreen: boolean) => {
        const recordId = rowData.recordId;
        const docNo = String(rowData.docNo);
        const formName = String(rowData.form.caption);
        const date = rowData.creationDate ? new Date(rowData.creationDate as string) : undefined;
        const title = constructRecordTitle(formName, docNo, '');
        const details = <RecordTitle title={title} date={date} />;

        if (module) {
          activeRecordReference.current?.addActiveRecord(String(recordId), module.icon, details, isFullScreen, {
            title,
            recordId,
            viewId: module.viewId,
            moduleIcon: module.icon,
            formId: rowData?.form?.id,
            formName: formName,
            docNo,
            status: '',
          });
        }
      };

      const onClick = () => {
        rowClickHandler(handleClick);
      };

      return (
        <div className="flex w-full" onClick={onClick}>
          <div className="flex flex-grow items-center" onClick={props.onRowClicked}>
            {children}
          </div>
        </div>
      );
    },
    [modulesMap, userState.user?.currentSchema]
  );

  const gridSettings: DataGridSettings = useMemo(() => {
    return {
      columns: globalSearchColumns,
      autoSizeColumns: true,
      fullHeight: true,
      filterLocation: DataSearchLocation.Api,
    };
  }, []);

  const onDataLoadCompleted = () => {
    if (activeRecordId) {
      gridRef.current?.api.data.addSelectedRows([{ id: activeRecordId }], true);
    }
  };

  const onDataGridRefCreated = (dataGridRef: DataGridRef) => {
    gridRef.current = dataGridRef;
    setDataGridReference(dataGridRef);
  };

  return (
    <div className="flex h-full w-full flex-col gap-3">
      {modulesMap && (
        <DataGrid
          key={searchValue}
          ref={(gridRef) => {
            if (gridRef) onDataGridRefCreated(gridRef);
          }}
          data={gridData}
          gridSettings={gridSettings}
          rowTemplate={rowTemplate}
          customSettingsMenuItems={[]}
        />
      )}
    </div>
  );
}
